import { useCallback, useState } from "react";
import { showTestJobsStore } from "@gui/infrastructure/pass-through/storage";

export interface ShowTestJobsToggleViewModel {
	showTestJobs: boolean;
	toggle: () => void;
}

/** The two browser effects the toggle needs; a parameter only so tests can observe them. */
export interface ShowTestJobsBrowser {
	confirm: (message: string) => boolean;
	reload: () => void;
}

const realBrowser: ShowTestJobsBrowser = {
	confirm: (message) => window.confirm(message),
	reload: () => window.location.reload(),
};

const RELOAD_WARNING = "The page will reload, so any unsaved changes on it will be lost.";
export const SHOW_PROMPT = `Show test jobs? ${RELOAD_WARNING}`;
export const HIDE_PROMPT = `Hide test jobs? ${RELOAD_WARNING}`;

/**
 * Flips the persisted "Show Test Jobs" switch, then reloads the page.
 *
 * The switch changes what the API returns (see `showTestJobsStore`), and every list already on
 * screen — the issues queues, their counts, the scheduling backlog — was fetched under the old
 * setting. A reload is the one refresh that cannot miss a cache, the same reasoning as the hard
 * navigation on logout.
 *
 * The reload is also why it asks first: the item can be clicked from any page, and whatever that
 * page holds unsaved (a scheduler's pending shifts, a half-edited job) goes with it. The toggle
 * cannot know what each page is holding, so it always confirms rather than guess.
 */
export function useShowTestJobsToggleViewModel(
	browser: ShowTestJobsBrowser = realBrowser,
): ShowTestJobsToggleViewModel {
	const [showTestJobs, setShowTestJobs] = useState(() => showTestJobsStore.get());

	const toggle = useCallback(() => {
		const next = !showTestJobs;
		if (!browser.confirm(next ? SHOW_PROMPT : HIDE_PROMPT)) return;
		showTestJobsStore.set(next);
		setShowTestJobs(next);
		browser.reload();
	}, [showTestJobs, browser]);

	return { showTestJobs, toggle };
}
